import UpdateNotice from './UpdateNotice';
import ThemePicker from './ThemePicker';
import Notifications from './Notifications';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isChatRoute } from '../lib/chatNavigation';
import { useAuth } from '../contexts/AuthContext';
import { Home, Lightbulb, MapPin, FolderOpen, FileText, UserRound, LogOut, Menu, X, Settings, Users, Mail, HeartHandshake, Crown, History, BarChart3, Plus, ShieldCheck, ClipboardCheck, Layers, Lock, Flag, CalendarCheck } from 'lucide-react';
import Logo from './Logo';
import '../styles/operations.css';
import '../styles/profileExperience.css';
import { COPYRIGHT, roleHome, STAFF_HOME } from '../config/brand';
import { isStaffOnly, isStaffUser, staffSections } from '../lib/permissions';
import { venueLocked, venueLockReason } from '../lib/entitlements';

const organizerNav = [
  { path: '/organizer/home', label: 'Ana sayfa', icon: Home },
  { path: '/organizer/home?view=chats', label: 'Asistan sohbetleri', icon: History },
  { path: '/organizer/idea', label: 'Fikirlerim', icon: Lightbulb },
  { path: '/organizer/home?view=projects', label: 'Projelerim', icon: FolderOpen },
  { path: '/organizer/search', label: 'Mekân bul', icon: MapPin },
  { path: '/organizer/applications', label: 'Taleplerim', icon: FileText },
  { path: '/organizer/profile', label: 'Profilim', icon: UserRound },
  { path: '/organizer/cooperation', label: 'Sonuçlarım', icon: BarChart3 },
  { path: '/advertising', label: 'Tanıtım & Büyüme', icon: Mail },
];
const venueNav = [
  { path: '/dashboard', label: 'Ana sayfa', icon: Home },
  { path: '/dashboard?view=chats', label: 'Asistan sohbetleri', icon: History },
  { path: '/venue/profile', label: 'Profilim', icon: UserRound },
  { path: '/venue/applications', label: 'Taleplerim', icon: FileText },
  { path: '/venue/individual-requests', label: 'Rezervasyon talepleri', icon: Mail },
  { path: '/venue/discovery', label: 'Organizatör bul', icon: Users, capability: 'organizer_search' },
  { path: '/venue/idea', label: 'Fikirlerim', icon: Lightbulb, capability: 'idea_generate' },
  { path: '/venue/matches', label: 'Eşleşmelerim', icon: HeartHandshake },
  { path: '/venue/cooperation', label: 'Sonuçlarım', icon: BarChart3 },
  { path: '/advertising', label: 'Tanıtım & Büyüme', icon: Mail },
];
const adminNav = [
  { path: '/dashboard', label: 'Genel bakış', icon: Home },
  { path: '/admin/users', label: 'Üyeler', icon: Users },
  { path: '/admin/venues', label: 'Mekânlar', icon: MapPin },
  { path: '/admin/venue-invites', label: 'Davetler', icon: Mail },
  { path: '/admin/organizers', label: 'Organizatörler', icon: Users },
  { path: '/admin/pool', label: 'Havuz Yönetimi', icon: Layers },
  { path: '/admin/reviews', label: 'Onay Bekleyenler', icon: ClipboardCheck },
  { path: '/admin/reports', label: 'Şikâyetler', icon: Flag },
  { path: '/admin/event-reviews', label: 'Etkinlik doğrulamaları', icon: CalendarCheck },
  { path: '/admin/interests', label: 'Başvurular', icon: FileText },
  { path: '/admin/matches', label: 'Eşleşmeler', icon: HeartHandshake },
  { path: '/admin/staff', label: 'Ekip ve Yetkiler', icon: ShieldCheck },
  { path: '/admin/settings', label: 'Ayarlar', icon: Settings },
];
// Staff-only accounts: no member, plan, settings, invite, application or match pages (UI-05).
const SECTION_ICONS = { reviews: ClipboardCheck, pool: Layers, reports: Flag, events: CalendarCheck };
const staffNav = user => [{ path: STAFF_HOME, label: 'Personel ana sayfası', icon: Home },
  ...staffSections(user).map(section => ({ ...section, icon: SECTION_ICONS[section.icon] || ShieldCheck }))];
const staffPanelLink = { path: STAFF_HOME, label: 'Personel paneli', icon: ShieldCheck, testId: 'nav-staff-panel' };
export const profileDestination = role => role === 'organizer' ? '/organizer/profile' : role === 'venue' ? '/venue/profile' : role === 'admin' ? '/admin/settings' : role === 'pool_manager' ? STAFF_HOME : '/login';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(null);
  const [viewportTop, setViewportTop] = useState(0);
  const menuButton = useRef(null);
  const isOrganizer = user?.role === 'organizer';
  const staffOnly = isStaffOnly(user);
  const memberStaff = !staffOnly && isStaffUser(user);
  const profilePath = profileDestination(user?.role);
  const profileLabel = user?.role === 'admin' ? 'Yönetim ayarlarını aç' : staffOnly ? 'Personel ana sayfasını aç' : 'Profilimi aç';
  const memberNav = isOrganizer ? organizerNav : user?.role === 'venue' ? venueNav : user?.role === 'admin' ? adminNav : staffOnly ? staffNav(user) : [];
  const navItems = memberStaff ? [...memberNav, staffPanelLink] : memberNav;
  const projectsView = new URLSearchParams(location.search).get('view') === 'projects';
  const workspaceView = (isOrganizer && location.pathname === '/organizer/home' && !['projects', 'overview'].includes(new URLSearchParams(location.search).get('view')))
    || (user?.role === 'venue' && location.pathname === '/dashboard' && new URLSearchParams(location.search).get('view') !== 'overview');
  const focusedChat = workspaceView && isChatRoute(location.search);
  const active = (path) => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?');
      const view = new URLSearchParams(search).get('view');
      return (location.pathname === pathname && new URLSearchParams(location.search).get('view') === view) || (view === 'projects' && location.pathname.startsWith('/organizer/projects/'));
    }
    if (path === '/organizer/home' || path === '/dashboard') return location.pathname === path && !projectsView && new URLSearchParams(location.search).get('view') !== 'chats';
    return location.pathname === path;
  };
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    const escape = event => { if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); } };
    if (menuOpen) window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [menuOpen]);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    let largestHeight = window.innerHeight;
    let wasKeyboardOpen = false;
    const update = () => {
      largestHeight = Math.max(largestHeight, window.innerHeight);
      const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]');
      wasKeyboardOpen = Boolean((editing || wasKeyboardOpen) && Math.abs(viewport.scale - 1) < 0.01 && largestHeight - viewport.height > 150);
      setKeyboardOpen(wasKeyboardOpen); setViewportHeight(viewport.height); setViewportTop(Math.max(0, viewport.offsetTop || 0));
    };
    const reset = () => { largestHeight = window.innerHeight; wasKeyboardOpen = false; update(); };
    viewport.addEventListener('resize', update); viewport.addEventListener('scroll', update);
    window.addEventListener('resize', update); document.addEventListener('focusin', update); document.addEventListener('focusout', update); window.addEventListener('orientationchange', reset);
    update();
    return () => {
      viewport.removeEventListener('resize', update); viewport.removeEventListener('scroll', update);
      window.removeEventListener('resize', update); document.removeEventListener('focusin', update); document.removeEventListener('focusout', update); window.removeEventListener('orientationchange', reset);
    };
  }, []);
  // A6: ücretsiz mekânda ücretli özellik menüleri kilitli görünür (sayfa açıklama + Pro bağlantısı gösterir; sunucu 403).
  const isLocked = item => !!item.capability && venueLocked(user, item.capability);
  // Kilit nedeni: ücretli ama profili onaysız/gizli mekânda onay, ücretsiz mekânda ücretli üyelik.
  const lockText = venueLockReason(user) === 'approval' ? 'profil onayından sonra açılır' : 'ücretli üyelikle açılır';
  const navLink = (item, mobile = false) => {
    const Icon = item.icon;
    const locked = isLocked(item);
    return <Link key={item.path} to={item.path} aria-current={active(item.path) ? 'page' : undefined}
      className={`${mobile ? 'mobile-nav-link' : 'workspace-nav-link'} ${active(item.path) ? 'is-active' : ''}`}
      data-testid={item.testId || `nav-${item.path.replace(/\//g, '-')}`} data-locked={locked ? 'true' : undefined}
      title={locked ? lockText.charAt(0).toUpperCase() + lockText.slice(1) : undefined}>
      <Icon size={21} strokeWidth={1.7} aria-hidden="true" /><span>{item.label}</span>
      {locked && <><Lock size={14} aria-hidden="true" className="nav-lock-icon" /><span className="sr-only"> ({lockText})</span></>}
    </Link>;
  };
  const mobileItems = isOrganizer ? [organizerNav[0], { ...organizerNav[5], label: 'Taleplerim' }, { ...organizerNav[4], label: 'Mekân bul' }, { ...organizerNav[6], label: 'Profilim' }]
    : user?.role === 'venue' ? [venueNav[0], { ...venueNav[3], label: 'Taleplerim' }, { ...venueNav[4], label: 'Organizatör bul' }, { ...venueNav[2], label: 'Profilim' }]
      : staffOnly ? navItems.slice(0, 4) : [adminNav[0], adminNav[1], adminNav[3], adminNav[4]];
  const adminLike = user?.role === 'admin' || staffOnly;
  const primaryPaths = [...(isOrganizer ? ['/organizer/home', '/organizer/search', '/organizer/applications', '/organizer/profile'] : ['/dashboard', '/venue/discovery', '/venue/applications', '/venue/profile']), ...(memberStaff ? [STAFF_HOME] : [])];
  const primaryItems = adminLike ? navItems : primaryPaths.map(path => navItems.find(item => item.path === path)).filter(Boolean);
  const secondaryItems = adminLike ? [] : navItems.filter(item => !primaryPaths.includes(item.path));
  const roleLabel = user?.role === 'admin' ? 'Yönetim' : staffOnly ? 'Personel' : isOrganizer ? 'Organizatör' : 'Mekân';
  return <div className={`workspace-shell${keyboardOpen ? ' keyboard-open' : ''}${workspaceView ? ' workspace-chat-shell' : ''}${focusedChat ? ' workspace-chat-focused' : ''}`} style={workspaceView && viewportHeight ? { '--workspace-viewport-height': `${viewportHeight}px`, '--workspace-viewport-top': `${viewportTop}px` } : undefined}>
    <UpdateNotice />
    <a className="skip-link" href="#main-content">İçeriğe geç</a>
    <aside className="workspace-sidebar">
      <Link to={roleHome(user?.role)} className="workspace-logo" aria-label="MekandaPro · Ana sayfa"><Logo /></Link>
      <p className="workspace-role">{roleLabel}</p>
      <nav aria-label="Ana menü">{primaryItems.map(item => navLink(item))}{secondaryItems.length > 0 && <details className="workspace-secondary-nav" open={secondaryItems.some(item => active(item.path)) || undefined}><summary>Diğer araçlar</summary>{secondaryItems.map(item => navLink(item))}</details>}</nav>
      <div className="sidebar-bottom">
        {!staffOnly && <Link to="/pro" className="workspace-plan"><Crown size={19} /><span>Üyelik ve kullanım hakları</span></Link>}
        <div className="workspace-user">
          <Link to={profilePath} className="workspace-profile-link" aria-label={`${user?.name || 'Hesabım'} · ${profileLabel}`} data-testid="sidebar-profile-link">
            <span className="user-initial" aria-hidden="true">{user?.name?.[0]?.toLocaleUpperCase('tr') || 'M'}</span><span><strong>{user?.name}</strong><small>{roleLabel}</small></span>
          </Link>
          <button type="button" onClick={logout} aria-label="Çıkış yap" className="icon-button"><LogOut size={19} /></button>
        </div>
      </div>
    </aside>
    <div className="workspace-body">
      <header className="workspace-header" data-testid="site-header">
        <Link to={roleHome(user?.role)} className="mobile-wordmark" data-testid="layout-logo" aria-label="MekandaPro · Ana sayfa"><Logo size="md" /></Link>
        <span className="desktop-breadcrumb">Çalışma alanım <span>/</span> {navItems.find(item => active(item.path))?.label || roleLabel}</span>
        <div className="header-actions"><span className="header-theme-control"><ThemePicker /></span><Notifications />
          <Link to={profilePath} className="desktop-user-name" data-testid="layout-user-name" aria-label={`${user?.name || 'Hesabım'} · ${profileLabel}`} title={profileLabel}>{user?.name}</Link>
          <button type="button" ref={menuButton} className="icon-button mobile-menu-toggle" aria-label={menuOpen ? 'Menüyü kapat' : 'Menüyü aç'} aria-expanded={menuOpen} aria-controls="mobile-account-menu" onClick={() => setMenuOpen(value => !value)}>{menuOpen ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
      </header>
      {menuOpen && <nav id="mobile-account-menu" className="mobile-account-menu" aria-label="Tüm sayfalar">
        {navItems.map(item => navLink(item))}
        <div className="mobile-theme-control"><span>Görünüm</span><ThemePicker /></div>
        {!staffOnly && <Link to="/pro" className="workspace-nav-link"><Crown size={21} />Üyelik ve kullanım hakları</Link>}
        <button type="button" onClick={logout} className="workspace-nav-link" data-testid="layout-logout-button"><LogOut size={21} />Çıkış yap</button>
      </nav>}
      <main id="main-content" tabIndex={-1} className="workspace-main">{children}</main>
      <footer className="workspace-footer"><span data-testid="footer-copyright">{COPYRIGHT}</span><span>Fikirden etkinliğe.</span></footer>
    </div>
    <nav className={`mobile-bottom-nav${adminLike ? ' is-admin' : ''}`} aria-label="Mobil ana menü">
      {staffOnly ? mobileItems.map(item => navLink(item, true)) : <>
      {mobileItems.slice(0, 2).map(item => navLink(item, true))}
      {user?.role === 'admin' ? navLink(mobileItems[2], true) : <Link className="mobile-action-new" to={isOrganizer ? '/organizer/idea?new=1' : '/venue/discovery?new=1'} aria-label={isOrganizer ? 'Yeni etkinlik fikri oluştur' : venueLocked(user, 'organizer_search') ? `Organizatör araması ${lockText}` : 'Yeni organizatör araması başlat'} data-testid="mobile-new-action"><span><Plus size={25} aria-hidden="true" /></span><small>{isOrganizer ? 'Yeni etkinlik' : 'Yeni arama'}</small></Link>}
      {mobileItems.slice(user?.role === 'admin' ? 3 : 2).map(item => navLink(item, true))}
      </>}
    </nav>
  </div>;
};
export default Layout;
